import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  Optional,
  Self,
} from '@angular/core';
import { ControlContainer, NgControl } from '@angular/forms';
import {
  allowControlKeys,
  isDigitKey,
  isLowercaseLetterKey,
  isUppercaseAlphanumericKey,
} from '../utils/input-key-restrict.util';
import {
  CODIGO_TIPO_DOC_CC,
  CODIGO_TIPO_DOC_CE,
  CODIGO_TIPO_DOC_PA,
  CODIGO_TIPO_DOC_PPT,
} from '../utils/validation.constants';
import type { TipoDocIdentidadParaValidacion } from '../utils/form-validators.util';

const DEFAULT_MAX_LENGTH = 20;
const KEYS_ALLOWED_AT_MAX = new Set<string>(['Backspace', 'Delete']);

/**
 * Restringe el input de número de documento según el tipo seleccionado:
 * - CC (Cédula): solo dígitos.
 * - CE/PA/PPT: mayúsculas, números (y guión para CE/PA). Las minúsculas se convierten a mayúsculas.
 *
 * Uso: <input appNumeroDocumentoPorTipo [tipos]="documentos" formControlName="numeroDocumento" />
 */
@Directive({
  selector: 'input[appNumeroDocumentoPorTipo]',
  standalone: false, // NOSONAR: usado desde SharedModule; migración a standalone en otro cambio
})
export class NumeroDocumentoPorTipoDirective {
  @Input() idTipoDocControlName = 'idTipoDocIdentidad';
  @Input() tipos: TipoDocIdentidadParaValidacion[] = [];
  /** Si es false, no se permite pegar (ej. campo confirmar número de documento). Por defecto true. */
  @Input() allowPaste = true;

  constructor(
    private readonly el: ElementRef<HTMLInputElement>,
    @Optional() private readonly controlContainer: ControlContainer,
    @Optional() @Self() private readonly ngControl: NgControl
  ) {}

  private getCodigo(): string | null {
    const form = this.controlContainer?.control;
    if (!form) return null;
    const idTipoRaw = form.get(this.idTipoDocControlName)?.value;
    if (idTipoRaw === null || idTipoRaw === undefined || idTipoRaw === '') return null;
    const idTipoNum = Number(idTipoRaw);
    const tipo = this.tipos.find(
      (t) => t.idTipoDocIdentidad === idTipoNum || t.idTipoDocIdentidad === idTipoRaw
    );
    return tipo?.codigo ?? null;
  }

  private getMaxLength(): number {
    const max = this.el.nativeElement.maxLength;
    return max > 0 ? max : DEFAULT_MAX_LENGTH;
  }

  private applyMaxLength(value: string): string {
    const max = this.getMaxLength();
    return value.length <= max ? value : value.slice(0, max);
  }

  private filterValueByCodigo(value: string, codigo: string | null): string {
    if (!codigo) return value;
    let filtered: string;
    if (codigo === CODIGO_TIPO_DOC_CC) {
      filtered = value.replaceAll(/\D/g, '');
    } else if (codigo === CODIGO_TIPO_DOC_CE || codigo === CODIGO_TIPO_DOC_PA || codigo === CODIGO_TIPO_DOC_PPT) {
      filtered = value.replaceAll(/[^A-Za-z0-9]/g, '').toUpperCase();
    } else {
      return value;
    }
    return this.applyMaxLength(filtered);
  }

  private insertAtCursor(input: HTMLInputElement, text: string): void {
    const max = this.getMaxLength();
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const value = input.value;
    const newValue = this.applyMaxLength(
      value.slice(0, start) + text + value.slice(end)
    );
    if (newValue === value && start === end) return;
    this.ngControl?.control?.setValue(newValue, {
      emitEvent: true,
      emitModelToViewChange: true,
    });
    const newPos = Math.min(start + text.length, newValue.length, max);
    setTimeout(() => input.setSelectionRange(newPos, newPos));
  }

  private isAtMaxLength(input: HTMLInputElement | null): boolean {
    const len = input?.value?.length ?? 0;
    return this.getMaxLength() <= len;
  }

  private blockKeyIfAtMax(event: KeyboardEvent, input: HTMLInputElement | null): boolean {
    if (!this.isAtMaxLength(input)) return false;
    if (KEYS_ALLOWED_AT_MAX.has(event.key)) return false;
    event.preventDefault();
    return true;
  }

  private handleKeyDownCC(event: KeyboardEvent, input: HTMLInputElement): void {
    if (this.blockKeyIfAtMax(event, input)) return;
    if (!isDigitKey(event.key)) event.preventDefault();
  }

  private handleKeyDownAlphanumeric(event: KeyboardEvent, input: HTMLInputElement): void {
    if (this.blockKeyIfAtMax(event, input)) return;
    if (isUppercaseAlphanumericKey(event.key)) return;
    if (isLowercaseLetterKey(event.key)) {
      event.preventDefault();
      if (this.ngControl) this.insertAtCursor(input, event.key.toUpperCase());
    } else {
      event.preventDefault();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (allowControlKeys(event)) return;

    const codigo = this.getCodigo();
    const input = event.target as HTMLInputElement;
    if (!input) return;

    if (codigo === CODIGO_TIPO_DOC_CC) {
      this.handleKeyDownCC(event, input);
      return;
    }
    if (codigo === CODIGO_TIPO_DOC_CE || codigo === CODIGO_TIPO_DOC_PA || codigo === CODIGO_TIPO_DOC_PPT) {
      this.handleKeyDownAlphanumeric(event, input);
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    if (!this.allowPaste) return;

    const codigo = this.getCodigo();
    if (!codigo) return;
    const input = event.target as HTMLInputElement;
    if (!input || !this.ngControl) return;

    const pasted = event.clipboardData?.getData('text') ?? '';
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const value = input.value;
    const newValue = this.filterValueByCodigo(
      value.slice(0, start) + pasted + value.slice(end),
      codigo
    );

    this.ngControl.control?.setValue(newValue, { emitEvent: true });
    const newPos = Math.min(start + pasted.length, newValue.length);
    setTimeout(() => input.setSelectionRange(newPos, newPos));
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const codigo = this.getCodigo();
    if (!codigo) return;

    const input = event.target as HTMLInputElement;
    const value = input.value;
    const filtered = this.filterValueByCodigo(value, codigo);
    if (value === filtered) return;

    this.ngControl?.control?.setValue(filtered, { emitEvent: false });
    setTimeout(() => input.setSelectionRange(filtered.length, filtered.length));
  }
}
